// Remboursement de notes de frais depuis une ligne du relevé (28/10/2026) :
// notes validées proposées, aperçu des écritures (une seule écriture de banque
// par ligne : celle de la ligne en attente, aucune si elle est déjà
// comptabilisée sur le compte de remboursement, OD depuis 471), confirmation.
import { useState, useEffect } from "react";
import api from "../services/api";

const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const frDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const MODE_LABEL = {
  ventilation: "Ligne en attente : son écriture de banque sera passée (comptes des personnes au débit, 512 au crédit).",
  rattachement: "Ligne déjà comptabilisée sur le compte de remboursement : aucune nouvelle écriture, les notes passent « remboursées ».",
  reclassement: "Ligne déjà comptabilisée sur 471 (à ventiler) : OD de reclassement vers les comptes des personnes, aucune écriture de banque.",
};

export default function ExpenseReimburseModal({ row, companyId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.expenses.reimburseLine(row.id)
      .then(d => { setData(d); setChecked(new Set(d.proposal?.expense_ids || [])); })
      .catch(e => setError(e.message));
  }, [row.id]);

  const total = data ? data.notes.filter(n => checked.has(n.id)).reduce((s, n) => s + n.amount, 0) : 0;
  const toggle = (id) => { setPreview(null); setChecked(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const run = async (fn) => { setBusy(true); setError(""); try { return await fn(); } catch (e) { setError(e.message); return null; } finally { setBusy(false); } };
  const doPreview = async () => { const r = await run(() => api.expenses.reimbursePreview(companyId, [...checked], row.id)); if (r) setPreview(r); };
  const doConfirm = async () => { const r = await run(() => api.expenses.reimburse(companyId, [...checked], row.id)); if (r) onDone(r); };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 760 }}>
        <div className="modal-header"><h3 className="modal-title">🧾 Remboursement de notes de frais</h3><button className="modal-close" onClick={onClose}>✕</button></div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>{frDate(row.date)} · {row.label} · <strong>{eur(Math.abs(row.amount))}</strong></div>
        {error && <div className="alert danger" style={{ marginBottom: 8 }}>{error}</div>}
        {data && <div className="alert info" style={{ marginBottom: 8, fontSize: 12.5 }}>{MODE_LABEL[data.mode]}{data.account ? ` (${data.account})` : ""}</div>}
        {data && data.proposal && <div style={{ fontSize: 12.5, marginBottom: 6 }}>💡 Proposition : {data.proposal.reason}. À vérifier avant de confirmer.</div>}
        {data && (data.notes.length ? (
          <table className="tbl"><thead><tr><th /><th>Note</th><th>Personne</th><th>Date</th><th>Description</th><th>Montant</th></tr></thead><tbody>
            {data.notes.map(n => (
              <tr key={n.id}><td><input type="checkbox" checked={checked.has(n.id)} onChange={() => toggle(n.id)} /></td>
                <td>{n.piece}</td><td>{n.user_name}</td><td>{frDate(n.date)}</td><td>{n.description}</td><td>{eur(n.amount)}</td></tr>
            ))}
          </tbody></table>
        ) : <div style={{ fontSize: 12.5 }}>Aucune note validée à rembourser{data.mode === "rattachement" ? ` sur le compte ${data.account}` : ""} (Comptabilité &amp; fiscal &gt; Notes de frais).</div>)}
        {data && <div style={{ fontSize: 13, marginTop: 8 }}>Total coché : <strong>{eur(total)}</strong> / virement {eur(data.amount)}{Math.abs(total - data.amount) >= 0.005 ? " — le total doit être égal au virement" : " ✓"}</div>}
        {preview && preview.entries.length > 0 && (
          <table className="tbl" style={{ marginTop: 8 }}><thead><tr><th>Journal</th><th>Compte</th><th>Débit</th><th>Crédit</th></tr></thead><tbody>
            {preview.entries.map((l, i) => <tr key={i}><td>{l.journal}</td><td>{l.account_code} {l.account_name}</td><td>{l.debit ? eur(l.debit) : ""}</td><td>{l.credit ? eur(l.credit) : ""}</td></tr>)}
          </tbody></table>
        )}
        {preview && !preview.entries.length && <div style={{ fontSize: 12.5, marginTop: 8 }}>Aucune écriture : la ligne porte déjà le remboursement.</div>}
        <div className="btn-group" style={{ justifyContent: "flex-end", marginTop: 10 }}>
          <button className="btn" onClick={onClose}>Fermer</button>
          <button className="btn" disabled={busy || !checked.size || !data || Math.abs(total - data.amount) >= 0.005} onClick={doPreview}>Aperçu</button>
          <button className="btn primary" disabled={busy || !preview} onClick={doConfirm}>Confirmer le remboursement</button>
        </div>
      </div>
    </div>
  );
}
